import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../services/api';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  background: white;
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: var(--gray-50);
    border-color: var(--gray-400);
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid var(--gray-200);
  padding: 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols || 2}, 1fr);
  gap: 16px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--gray-700);
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary-500);
    box-shadow: 0 0 0 3px var(--primary-100);
  }
  
  &:disabled {
    background: var(--gray-100);
    cursor: not-allowed;
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
  
  label {
    font-size: 14px;
    color: var(--gray-700);
    cursor: pointer;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid var(--gray-200);
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${({ $variant }) => $variant === 'primary' ? `
    background: var(--primary-600);
    color: white;
    border: none;
    
    &:hover {
      background: var(--primary-700);
    }
  ` : `
    background: white;
    color: var(--gray-700);
    border: 1px solid var(--gray-300);
    
    &:hover {
      background: var(--gray-50);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HelpText = styled.span`
  font-size: 12px;
  color: var(--gray-500);
`;

const CurrencyForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    symbol: '',
    decimal_places: 2,
    exchange_rate: 1.0,
    is_base: false,
    is_active: true,
  });

  useEffect(() => {
    if (isEdit) {
      fetchCurrency();
    }
  }, [id]);

  const fetchCurrency = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/currencies/${id}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching currency:', error);
      toast.error('Failed to load currency');
      navigate('/currencies');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code || !formData.name || !formData.symbol) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        decimal_places: parseInt(formData.decimal_places),
        exchange_rate: parseFloat(formData.exchange_rate),
      };
      
      if (isEdit) {
        await api.put(`/currencies/${id}`, payload);
        toast.success('Currency updated successfully');
      } else {
        await api.post('/currencies/', payload);
        toast.success('Currency created successfully');
      }
      
      navigate('/currencies');
    } catch (error) {
      console.error('Error saving currency:', error);
      toast.error(error.response?.data?.error || 'Failed to save currency');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <p>Loading...</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={() => navigate('/currencies')}>
          <FiArrowLeft />
        </BackButton>
        <Title>{isEdit ? 'Edit Currency' : 'Add Currency'}</Title>
      </PageHeader>

      <Card>
        <Form onSubmit={handleSubmit}>
          <FormRow>
            <FormGroup>
              <Label>Currency Code *</Label>
              <Input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="USD"
                maxLength={3}
                required
              />
              <HelpText>ISO 4217 3-letter code (e.g., USD, EUR, GBP)</HelpText>
            </FormGroup>
            
            <FormGroup>
              <Label>Currency Name *</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="US Dollar"
                required
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Symbol *</Label>
              <Input
                type="text"
                name="symbol"
                value={formData.symbol}
                onChange={handleChange}
                placeholder="$"
                maxLength={10}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Decimal Places</Label>
              <Input
                type="number"
                name="decimal_places"
                value={formData.decimal_places}
                onChange={handleChange}
                min={0}
                max={8}
              />
              <HelpText>Number of decimal places (0 for JPY, 2 for USD)</HelpText>
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Exchange Rate</Label>
              <Input
                type="number"
                name="exchange_rate"
                value={formData.exchange_rate}
                onChange={handleChange}
                step="0.000001"
                min={0}
              />
              <HelpText>Rate relative to base currency</HelpText>
            </FormGroup>
          </FormRow>

          <FormRow $cols={1}>
            <Checkbox>
              <input
                type="checkbox"
                id="is_base"
                name="is_base"
                checked={formData.is_base}
                onChange={handleChange}
              />
              <label htmlFor="is_base">Set as base currency</label>
            </Checkbox>
            
            <Checkbox>
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              <label htmlFor="is_active">Active</label>
            </Checkbox>
          </FormRow>

          <ButtonGroup>
            <Button type="button" onClick={() => navigate('/currencies')}>
              Cancel
            </Button>
            <Button type="submit" $variant="primary" disabled={saving}>
              <FiSave /> {saving ? 'Saving...' : 'Save Currency'}
            </Button>
          </ButtonGroup>
        </Form>
      </Card>
    </PageContainer>
  );
};

export default CurrencyForm;
